import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { passwordResetRedirectUrl } from "../lib/passwordRecovery";
import { COOLDOWN_KEYS, readCooldown, startCooldown } from "../lib/resendCooldown";
import { reportError } from "../lib/errorReporter";

const KEY = COOLDOWN_KEYS.profilePasswordReset;

export default function Profile() {
  const { t } = useTranslation();
  const { user, me } = useAuth();
  // The authenticated account e-mail is the ONLY possible destination. It is
  // rendered as text, never as an editable field, and never taken from input.
  const accountEmail = user?.email || me?.email || "";
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(() => readCooldown(KEY));
  const inFlight = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown(readCooldown(KEY)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const requestLink = useCallback(async () => {
    if (inFlight.current || busy || cooldown > 0 || !accountEmail) return;
    inFlight.current = true;
    setBusy(true);
    const { error: requestError } = await supabase.auth.resetPasswordForEmail(accountEmail, {
      redirectTo: passwordResetRedirectUrl(),
    });
    if (requestError) {
      // Never surface the raw provider error; the neutral confirmation stays.
      reportError("auth.password_reset_request");
    }
    inFlight.current = false;
    setBusy(false);
    startCooldown(KEY);
    setCooldown(readCooldown(KEY));
    setSent(true);
  }, [accountEmail, busy, cooldown]);

  return (
    <Layout title={t("profile.title")}>
      <div className="h1">{t("profile.title")}</div>
      <div style={{ height: 20 }} />

      <div className="qw-card" data-testid="profile-account-card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("profile.account_section")}</div>
        <div className="qw-grid cols-2">
          <div>
            <div className="qw-help">{t("profile.account_email")}</div>
            <div style={{ fontWeight: 500 }} data-testid="profile-account-email">
              {accountEmail || "—"}
            </div>
          </div>
          <div>
            <div className="qw-help">{t("profile.account_name")}</div>
            <div style={{ fontWeight: 500 }} data-testid="profile-account-name">
              {me?.full_name?.trim() || "—"}
            </div>
          </div>
        </div>
        <div className="qw-help" style={{ marginTop: 12 }} data-testid="profile-readonly-note">
          {t("profile.account_readonly_note")}
        </div>
      </div>

      <div style={{ height: 16 }} />

      <div className="qw-card" data-testid="profile-security-card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("profile.security_section")}</div>
        <div className="qw-help" style={{ marginBottom: 12 }}>
          {t("profile.security_lead")}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div className="qw-help">{t("profile.security_destination")}</div>
          <div style={{ fontWeight: 500 }} data-testid="profile-security-email">
            {accountEmail || "—"}
          </div>
        </div>
        <button
          type="button"
          className="qw-btn primary"
          onClick={requestLink}
          disabled={busy || cooldown > 0 || !accountEmail}
          data-testid="profile-send-password-link"
        >
          {busy ? t("profile.sending") : t("profile.send_reset_link")}
        </button>
        {sent && (
          <div
            className="qw-help"
            style={{ color: "var(--qw-success)", marginTop: 10 }}
            role="status"
            data-testid="profile-password-link-sent"
          >
            {t("profile.reset_link_sent")}
          </div>
        )}
        {cooldown > 0 && (
          <div className="qw-help" style={{ marginTop: 6 }} data-testid="profile-password-cooldown">
            {t("profile.cooldown", { seconds: cooldown })}
          </div>
        )}
      </div>
    </Layout>
  );
}
